import {
  Container,
  Typography,
  Chip,
  Box,
  Paper,
  Skeleton,
  List,
  ListItem,
  Card,
  IconButton,
  Tooltip,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import ReactMarkdown from "react-markdown";
import { forwardRef, useState, useEffect } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

const PROGRESS_STEPS = [
  { label: "Gathering background information", duration: 4000 },
  { label: "Reading relevant research papers", duration: 4500 },
  { label: "Highlighting important points", duration: 3800 },
  { label: "Comparing study methodologies", duration: 4200 },
  { label: "Summarizing key findings", duration: 4000 },
  { label: "Analyzing research gaps", duration: 3900 },
  { label: "Structuring the literature review", duration: 4100 },
  { label: "Inserting accurate citations", duration: 3700 },
  { label: "Double-checking references", duration: 3600 },
  { label: "Fine-tuning the final draft", duration: 38000 },
];

const ReviewDisplay = forwardRef(
  ({ survey, references, isLoading, metadata }, ref) => {
    const [copied, setCopied] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [showStep, setShowStep] = useState(false);

    useEffect(() => {
      let timeoutId;

      if (isLoading) {
        setShowStep(true);
        setCurrentStep(0);

        const cycleSteps = (stepIndex) => {
          if (!isLoading) return;

          timeoutId = setTimeout(() => {
            if (stepIndex < PROGRESS_STEPS.length - 1) {
              setCurrentStep(stepIndex + 1);
              cycleSteps(stepIndex + 1);
            } else {
              // Start over if still loading
              setCurrentStep(0);
              cycleSteps(0);
            }
          }, PROGRESS_STEPS[stepIndex].duration);
        };

        cycleSteps(0);
      } else {
        setShowStep(false);
      }

      return () => {
        clearTimeout(timeoutId);
      };
    }, [isLoading]);

    useEffect(() => {
      if (isLoading && ref.current) {
        ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, [isLoading]);

    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(survey);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy text:", err);
      }
    };

    const calculateWordCount = (text) => {
      if (!text) return 0;
      return text.trim().split(/\s+/).length;
    };

    const wordCount = calculateWordCount(survey);

    if (isLoading) {
      return (
        <Container
          ref={ref}
          maxWidth="md"
          sx={{
            py: { xs: 3, sm: 4, md: 5 },
            bgcolor: "#FFFFFF",
            borderRadius: 2,
            boxShadow: 1,
          }}
        >
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <CircularProgress size={40} thickness={4} />
          </Box>

          <Box sx={{ position: "relative", mb: 4 }}>
            <Typography
              variant="h6"
              color="primary"
              sx={{
                mb: 2,
                textAlign: "center",
                fontWeight: 500,
                opacity: showStep ? 1 : 0,
                transition: "opacity 0.3s ease-in-out",
              }}
            >
              {PROGRESS_STEPS[currentStep].label}
            </Typography>

            <LinearProgress
              variant="determinate"
              value={(currentStep + 1) * (100 / PROGRESS_STEPS.length)}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: "rgba(0, 0, 0, 0.05)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 4,
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            {[...Array(3)].map((_, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={20}
                sx={{
                  mb: 1,
                  borderRadius: 1,
                  animation: "pulse 1.5s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%": { opacity: 0.6 },
                    "50%": { opacity: 0.3 },
                    "100%": { opacity: 0.6 },
                  },
                }}
              />
            ))}
          </Box>
        </Container>
      );
    }

    if (!survey) return null;

    const formatReference = (ref) => {
      // Helper function to remove arXiv from any string
      const removeArXiv = (str) => {
        if (!str || typeof str !== "string") return str;
        return str.replace(/arXiv/gi, "").trim();
      };

      const parts = [];

      // Author
      if (ref.author) {
        parts.push(`${removeArXiv(ref.author)}`);
      }

      // Title
      if (ref.title) {
        parts.push(`"${removeArXiv(ref.title)}"`);
      }

      // Journal with optional volume and issue
      if (ref.journal) {
        let journalInfo = removeArXiv(ref.journal);
        if (ref.volume) {
          journalInfo += `, ${removeArXiv(ref.volume)}`;
          if (ref.issue) {
            journalInfo += `(${removeArXiv(ref.issue)})`;
          }
        }
        if (ref.pages) {
          journalInfo += `, ${removeArXiv(ref.pages)}`;
        }
        parts.push(journalInfo);
      }

      // Year
      if (ref.year) {
        parts.push(`(${removeArXiv(ref.year)})`);
      }

      return parts.join(". ");
    };

    const renderReferences = () => {
      if (!references || !Array.isArray(references)) return null;

      return (
        <Box
          sx={{
            mt: 4,
            pt: 4,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="h5"
            color="primary"
            gutterBottom
            sx={{
              fontSize: "1.15rem",
              fontWeight: 600,
            }}
          >
            References
          </Typography>
          <Paper
            elevation={0}
            sx={{
              maxHeight: "400px",
              overflow: "auto",
              bgcolor: "transparent",
              overflowX: "hidden",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: (theme) => theme.palette.primary.light,
                borderRadius: "10px",
                "&:hover": {
                  background: (theme) => theme.palette.primary.main,
                },
              },
            }}
          >
            <List sx={{ p: 0 }}>
              {references.map((ref, index) => (
                <ListItem
                  key={index}
                  sx={{
                    px: 0,
                    py: 1,
                  }}
                >
                  <Card
                    elevation={1}
                    sx={{
                      width: "100%",
                      p: 1.5,
                      transition: "all 0.2s ease-in-out",
                      borderLeft: "4px solid transparent",
                      "&:hover": {
                        elevation: 3,
                        transform: "translateX(4px)",
                        bgcolor: "rgba(0, 0, 0, 0.01)",
                        borderLeft: (theme) =>
                          `4px solid ${theme.palette.primary.main}`,
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.primary",
                        lineHeight: 1.5,
                        fontSize: "0.9rem",
                        "& strong": {
                          color: "primary.main",
                          fontWeight: 500,
                        },
                      }}
                    >
                      {formatReference(ref)}
                    </Typography>
                  </Card>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Box>
      );
    };

    return (
      <Container
        ref={ref}
        maxWidth="md"
        sx={{ py: { xs: 3, sm: 4, md: 5 }, bgcolor: "#FFFFFF" }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography
            variant="h4"
            fontWeight={700}
            color="primary"
            sx={{
              fontSize: { xs: "1.75rem", sm: "2rem" },
            }}
          >
            Literature Review
          </Typography>
          <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
            <IconButton onClick={handleCopy} color="primary">
              {copied ? <CheckIcon /> : <ContentCopyIcon />}
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Chip
            label={`${wordCount} words`}
            color="primary"
            variant="outlined"
            size="small"
            sx={{ mr: 1 }}
          />
          <Chip
            label={metadata?.citationStyle}
            color="primary"
            variant="outlined"
            size="small"
            sx={{ mr: 1 }}
          />
          <Chip
            label={`${metadata?.paperCount} papers`}
            color="primary"
            variant="outlined"
            size="small"
          />
        </Box>

        <Box
          sx={{
            "& h2": {
              fontSize: "1.15rem",
              fontWeight: 600,
              color: "primary.main",
              mt: 3.5,
              mb: 1.5,
            },
            "& p": {
              color: "#111827",
              lineHeight: 1.6,
              mb: 1.5,
              fontSize: "0.95rem",
            },
            "& blockquote": {
              borderLeft: "4px solid",
              borderColor: "primary.main",
              pl: 2,
              ml: 2,
              fontStyle: "italic",
              fontSize: "0.95rem",
            },
          }}
        >
          <ReactMarkdown>{survey}</ReactMarkdown>
        </Box>

        {renderReferences()}
      </Container>
    );
  }
);

export default ReviewDisplay;
